// Learning data for the tenant's KPI page (fase 4): how the video was watched and how each question went.
// Sent with the breef report. The tenant only ever sees totals for groups of at least 5 people.

export interface QuestionStat {
  index: number;
  attempts: number;
  timeMs: number;
  correctFirstTry: boolean;
  /** Answered right at all - false when the time ran out. The server scores the breef from this. */
  correct: boolean;
}

interface VideoStats {
  durationSec: number;
  watchedSec: number;
  maxPositionSec: number;
  percentWatched: number;
  seeks: number;
  skippedForwardSec: number;
  completed: boolean;
}

interface LearningStats {
  video?: VideoStats;
  questions: QuestionStat[];
}

const KEY = "learningStats";
const round = (value: number) => Math.round(value * 10) / 10;

const load = (): LearningStats => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "") as LearningStats;
  } catch {
    return { questions: [] };
  }
};
const save = (stats: LearningStats) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(stats));
  } catch {
    // no storage: the report is sent without learning data
  }
};

/** A new breef starts (invite link opened) */
export const resetLearningStats = () => save({ questions: [] });

export const recordQuestion = (stat: QuestionStat) => {
  const stats = load();
  save({ ...stats, questions: [...stats.questions.filter((question) => question.index !== stat.index), { ...stat, timeMs: Math.round(stat.timeMs) }] });
};

// Video: time actually played, jumps forward (skipping) and the furthest point reached
let lastPosition = 0;
export const videoTracker = {
  duration(seconds: number) {
    const stats = load();
    save({ ...stats, video: { ...emptyVideo(), ...stats.video, durationSec: round(seconds) } });
  },
  progress(playedSeconds: number) {
    const stats = load();
    const video = { ...emptyVideo(), ...stats.video };
    const step = playedSeconds - lastPosition;
    // Normal playback reports about 1 second at a time; a bigger jump forward is a skip
    if (step > 0 && step <= 2.5) video.watchedSec = round(video.watchedSec + step);
    else if (step > 2.5) video.skippedForwardSec = round(video.skippedForwardSec + step);
    lastPosition = playedSeconds;
    video.maxPositionSec = round(Math.max(video.maxPositionSec, playedSeconds));
    video.percentWatched = video.durationSec ? Math.min(100, Math.round((video.watchedSec * 100) / video.durationSec)) : 0;
    save({ ...stats, video });
  },
  seek(seconds: number) {
    const stats = load();
    const video = { ...emptyVideo(), ...stats.video };
    video.seeks += 1;
    if (seconds > lastPosition + 2.5) video.skippedForwardSec = round(video.skippedForwardSec + (seconds - lastPosition));
    lastPosition = seconds;
    save({ ...stats, video });
  },
  ended() {
    const stats = load();
    const video = { ...emptyVideo(), ...stats.video };
    video.completed = video.percentWatched >= 90;
    save({ ...stats, video });
  },
};
const emptyVideo = (): VideoStats => ({ durationSec: 0, watchedSec: 0, maxPositionSec: 0, percentWatched: 0, seeks: 0, skippedForwardSec: 0, completed: false });

/** The learning data for the breef report (left out when there is none) */
export const learningPayload = () => {
  const stats = load();
  return {
    ...(stats.video && stats.video.durationSec > 0 ? { videoStats: stats.video } : {}),
    ...(stats.questions.length ? { questionStats: stats.questions } : {}),
  };
};
